import { articlesPath } from "@dashboard/articles/urls";
import { attributeListUrl } from "@dashboard/attributes/urls";
import { useUser } from "@dashboard/auth/useUser";
import { cartsListPath } from "@dashboard/carts/urls";
import { categoryListUrl } from "@dashboard/categories/urls";
import { collectionListUrl } from "@dashboard/collections/urls";
import { navigationLucideIconProps } from "@dashboard/components/icons";
import { configurationMenuUrl } from "@dashboard/configuration/urls";
import { getConfigMenuItemsPermissions } from "@dashboard/configuration/utils";
import { rippleNewCustomersView } from "@dashboard/customers/ripples/newCustomersView";
import { customerListUrl } from "@dashboard/customers/urls";
import { saleListUrl, voucherListUrl } from "@dashboard/discounts/urls";
import { SidebarAppAlert } from "@dashboard/extensions/components/AppAlerts/SidebarAppAlert";
import { useAppsAlert } from "@dashboard/extensions/components/AppAlerts/useAppsAlert";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import {
  extensionsAppSection,
  extensionsCustomSection,
  ExtensionsPaths,
  extensionsPluginSection,
} from "@dashboard/extensions/urls";
import { giftCardListUrl } from "@dashboard/giftCards/urls";
import { PermissionEnum } from "@dashboard/graphql";
import { rippleHomeWidgets } from "@dashboard/home/ripples/homeWidgets";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { DiscountsIcon } from "@dashboard/icons/Discounts";
import { HomeIcon } from "@dashboard/icons/Home";
import { MarketplaceIcon } from "@dashboard/icons/Marketplace";
import { ModelingIcon } from "@dashboard/icons/Modeling";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { TranslationsIcon } from "@dashboard/icons/Translations";
import { importPageUrl } from "@dashboard/imports/urls";
import { sectionNames } from "@dashboard/intl";
import { pageListPath } from "@dashboard/modeling/urls";
import { pageTypeListUrl } from "@dashboard/modelTypes/urls";
import { orderListUrl } from "@dashboard/orders/urls";
import { paymentLinksListUrl } from "@dashboard/paymentLinks/urls";
import { productListUrl } from "@dashboard/products/urls";
import { productTypeListUrl } from "@dashboard/productTypes/urls";
import { Ripple } from "@dashboard/ripples/components/Ripple";
import { SearchShortcut } from "@dashboard/search/SearchShortcut";
import { menuListUrl } from "@dashboard/structures/urls";
import { languageListUrl } from "@dashboard/translations/urls";
import { Box, Text } from "@saleor/macaw-ui-next";
import isEmpty from "lodash/isEmpty";
import {
  FileText,
  FileUp,
  FolderTree,
  Gift,
  Layers,
  Link,
  Puzzle,
  Search,
  ShoppingCart,
  SlidersHorizontal,
  Ticket,
} from "lucide-react";
import { useIntl } from "react-intl";

import { SidebarIconSlot } from "../../SidebarIconSlot";
import { createSettingsSubmenuItem } from "../settingsSubmenuItem";
import { type SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";

export function useMenuStructure() {
  const { handleAppsListItemClick, hasProblems } = useAppsAlert();

  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  const getExtensionsSection = (): SidebarMenuItem => ({
    icon: renderIcon(<MarketplaceIcon />),
    label: intl.formatMessage(sectionNames.extensions),
    permissions: [],
    id: "installed-extensions",
    url: ExtensionsPaths.installedExtensions,
    type: "itemGroup",
    endAdornment: <SidebarAppAlert hasNewFailedAttempts={hasProblems} />,
    onClick: () => handleAppsListItemClick(new Date().toISOString()),
    children: [
      {
        label: (
          <Box display="flex" alignItems="center" gap={3}>
            <Text size={3} fontWeight="medium">
              {intl.formatMessage(sectionNames.installedExtensions)}
            </Text>
            <SidebarAppAlert hasNewFailedAttempts={hasProblems} small />
          </Box>
        ),
        id: "installed-extensions",
        url: ExtensionsPaths.installedExtensions,
        matchUrls: [
          ExtensionsPaths.installedExtensions,
          extensionsCustomSection,
          extensionsAppSection,
          extensionsPluginSection,
        ],
        permissions: [],
        type: "item",
      },
      {
        label: intl.formatMessage(sectionNames.exploreExtensions),
        id: "explore-extensions",
        url: ExtensionsPaths.exploreExtensions,
        permissions: [],
        type: "item" as const,
      },
    ],
  });

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      label: intl.formatMessage(sectionNames.home),
      id: "home",
      url: "/home",
      type: "item",
      endAdornment: <Ripple model={rippleHomeWidgets} />,
    },
    {
      icon: renderIcon(<Search {...navigationLucideIconProps} />),
      label: (
        <Box display="flex" alignItems="center" gap={2}>
          <Text size={3} fontWeight="medium">
            {intl.formatMessage(sectionNames.search)}
          </Text>
          <SearchShortcut />
        </Box>
      ),
      id: "search",
      url: "/search",
      permissions: [
        PermissionEnum.MANAGE_PRODUCTS,
        PermissionEnum.MANAGE_PAGES,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
        PermissionEnum.MANAGE_ORDERS,
      ],
      type: "item",
    },
    {
      id: "section-catalog",
      label: intl.formatMessage(sectionNames.catalog),
      type: "divider",
      paddingY: 3,
    },
    {
      id: "products",
      label: intl.formatMessage(sectionNames.products),
      url: productListUrl(),
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      icon: renderIcon(<ProductsIcon />),
      type: "item",
    },
    {
      id: "categories",
      label: intl.formatMessage(sectionNames.categories),
      url: categoryListUrl(),
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      icon: renderIcon(<FolderTree {...navigationLucideIconProps} />),
      type: "item",
    },
    {
      id: "collections",
      label: intl.formatMessage(sectionNames.collections),
      url: collectionListUrl(),
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      icon: renderIcon(<Layers {...navigationLucideIconProps} />),
      type: "item",
    },
    {
      id: "attributes",
      label: intl.formatMessage(sectionNames.attributes),
      url: attributeListUrl(),
      permissions: [
        PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
      ],
      icon: renderIcon(<SlidersHorizontal {...navigationLucideIconProps} />),
      type: "item",
    },
    {
      icon: renderIcon(<FileUp {...navigationLucideIconProps} />),
      label: intl.formatMessage(sectionNames.imports),
      permissions: [PermissionEnum.MANAGE_PRODUCTS],
      id: "imports",
      url: importPageUrl,
      type: "item",
    },
    {
      id: "product-types",
      label: intl.formatMessage(sectionNames.productTypes),
      url: productTypeListUrl(),
      permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
      icon: renderIcon(<ConfigurationIcon />),
      type: "item",
    },
    ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem).filter(() =>
      user?.userPermissions?.some(permission =>
        [
          PermissionEnum.MANAGE_PRODUCTS,
          PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
          PermissionEnum.MANAGE_GIFT_CARD,
        ].includes(permission.code),
      ),
    ),
    {
      id: "section-sales",
      label: intl.formatMessage({ defaultMessage: "Sales", id: "c8nvms" }),
      type: "divider",
      paddingY: 3,
    },
    {
      icon: renderIcon(<OrdersIcon />),
      label: intl.formatMessage(sectionNames.orders),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<Link {...navigationLucideIconProps} />),
      label: intl.formatMessage(sectionNames.paymentLinks),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "payment-links",
      url: paymentLinksListUrl,
      type: "item",
    },
    {
      icon: renderIcon(<ShoppingCart {...navigationLucideIconProps} />),
      label: intl.formatMessage(sectionNames.carts),
      permissions: [PermissionEnum.MANAGE_CHECKOUTS],
      id: "carts",
      url: cartsListPath,
      type: "item",
    },
    ...mapToExtensionsItems(extensions.NAVIGATION_ORDERS, appExtensionsHeaderItem)
      .filter(
        item =>
          item.type !== "divider" &&
          user?.userPermissions?.some(permission =>
            [PermissionEnum.MANAGE_ORDERS, PermissionEnum.MANAGE_CHECKOUTS].includes(
              permission.code,
            ),
          ),
      )
      .map(item => ({ ...item, icon: renderIcon(<Puzzle {...navigationLucideIconProps} />) })),
    {
      id: "section-customers-marketing",
      label: intl.formatMessage({
        defaultMessage: "Customers & marketing",
        id: "UdWcoU",
      }),
      type: "divider",
      paddingY: 3,
    },
    {
      icon: renderIcon(<CustomersIcon />),
      label: intl.formatMessage(sectionNames.customers),
      // Sidebar gating uses any-of matching, so users with only MANAGE_ORDERS
      // or MANAGE_STAFF can navigate to customer pages in read-only mode while
      // edit affordances remain hidden inside the section itself.
      permissions: [
        PermissionEnum.MANAGE_USERS,
        PermissionEnum.MANAGE_ORDERS,
        PermissionEnum.MANAGE_STAFF,
      ],
      endAdornment: <Ripple model={rippleNewCustomersView} />,
      id: "customers",
      url: customerListUrl(),
      type: "item",
    },
    {
      id: "promotions",
      label: intl.formatMessage(sectionNames.promotions),
      url: saleListUrl(),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      icon: renderIcon(<DiscountsIcon />),
      type: "item",
    },
    {
      id: "vouchers",
      label: intl.formatMessage(sectionNames.vouchers),
      url: voucherListUrl(),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      icon: renderIcon(<Ticket {...navigationLucideIconProps} />),
      type: "item",
    },
    {
      id: "giftCards",
      label: intl.formatMessage(sectionNames.giftCards),
      url: giftCardListUrl(),
      permissions: [PermissionEnum.MANAGE_GIFT_CARD],
      icon: renderIcon(<Gift {...navigationLucideIconProps} />),
      type: "item",
    },
    ...mapToExtensionsItems(extensions.NAVIGATION_CUSTOMERS, appExtensionsHeaderItem).filter(() =>
      user?.userPermissions?.some(permission =>
        [
          PermissionEnum.MANAGE_USERS,
          PermissionEnum.MANAGE_ORDERS,
          PermissionEnum.MANAGE_STAFF,
        ].includes(permission.code),
      ),
    ),
    ...mapToExtensionsItems(extensions.NAVIGATION_DISCOUNTS, appExtensionsHeaderItem).filter(() =>
      user?.userPermissions?.some(permission =>
        [PermissionEnum.MANAGE_DISCOUNTS].includes(permission.code),
      ),
    ),
    {
      id: "section-content",
      label: intl.formatMessage({ defaultMessage: "Content", id: "Jq3FDz" }),
      type: "divider",
      paddingY: 3,
    },
    {
      id: "articles",
      label: intl.formatMessage({ defaultMessage: "Articles", id: "3KNMbJ" }),
      url: articlesPath,
      permissions: [PermissionEnum.MANAGE_PAGES],
      icon: renderIcon(<FileText {...navigationLucideIconProps} />),
      type: "item",
    },
    {
      children: !isEmpty(extensions.NAVIGATION_TRANSLATIONS)
        ? [...mapToExtensionsItems(extensions.NAVIGATION_TRANSLATIONS, appExtensionsHeaderItem)]
        : undefined,
      icon: renderIcon(<TranslationsIcon />),
      label: intl.formatMessage(sectionNames.translations),
      permissions: [PermissionEnum.MANAGE_TRANSLATIONS],
      id: "translations",
      url: languageListUrl,
      type: !isEmpty(extensions.NAVIGATION_TRANSLATIONS) ? "itemGroup" : "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.models),
          id: "models",
          url: pageListPath,
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.structures),
          id: "structures",
          url: menuListUrl(),
          permissions: [PermissionEnum.MANAGE_MENUS],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_PAGES, appExtensionsHeaderItem),
        createSettingsSubmenuItem({
          id: "model-types",
          label: intl.formatMessage(sectionNames.modelTypes),
          url: pageTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
        }),
      ],
      icon: renderIcon(<ModelingIcon />),
      label: intl.formatMessage(sectionNames.modeling),
      permissions: [
        PermissionEnum.MANAGE_PAGES,
        PermissionEnum.MANAGE_MENUS,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
      ],
      id: "modeling",
      url: pageListPath,
      type: "itemGroup",
    },
    {
      id: "section-configuration",
      label: intl.formatMessage(sectionNames.configuration),
      type: "divider",
      paddingY: 3,
    },
    getExtensionsSection(),
    {
      icon: renderIcon(<ConfigurationIcon />),
      label: intl.formatMessage({ defaultMessage: "All settings", id: "IzEySe" }),
      permissions: getConfigMenuItemsPermissions(intl),
      id: "configure",
      url: configurationMenuUrl,
      type: "item",
    },
  ];
  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };
  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  // Top-level app links need their own icon in the collapsed sidebar rail.
  const navigableItems = menuItems
    .filter(item => item.id !== appExtensionsHeaderItem.id)
    .map(item =>
      item.id.startsWith("extension-")
        ? { ...item, icon: renderIcon(<Puzzle {...navigationLucideIconProps} />) }
        : item,
    );
  const filteredItems = navigableItems.reduce(
    (resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
      if (!isMenuItemPermitted(menuItem)) {
        return resultItems;
      }

      const { children } = menuItem;
      const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

      return [...resultItems, { ...menuItem, children: filteredChildren }];
    },
    [],
  );

  // Omit headings when permissions leave their section empty.
  return filteredItems.filter((item, index) => {
    if (item.type !== "divider") {
      return true;
    }

    const followingItems = filteredItems.slice(index + 1);
    const nextSection = followingItems.findIndex(next => next.id.startsWith("section-"));
    const sectionItems = nextSection < 0 ? followingItems : followingItems.slice(0, nextSection);

    return sectionItems.some(next => next.type !== "divider");
  });
}

function renderIcon(icon: React.ReactNode) {
  return <SidebarIconSlot>{icon}</SidebarIconSlot>;
}
